import { useEffect, useState } from 'react'

type UpdateState = 'idle' | 'updating' | 'ready' | 'reloading'

interface AppUpdateStore {
  state: UpdateState
  progress: number
}

type Listener = (store: AppUpdateStore) => void

// Module-level singleton — callable from outside React (main.tsx, versionChecker.ts)
let store: AppUpdateStore = { state: 'idle', progress: 0 }
const listeners = new Set<Listener>()

function notify(): void {
  listeners.forEach((l) => l(store))
}

function setStore(next: Partial<AppUpdateStore>): void {
  store = { ...store, ...next }
  notify()
}

let progressTimer: ReturnType<typeof setInterval> | null = null
let stuckTimeout: ReturnType<typeof setTimeout> | null = null
let currentRegistration: ServiceWorkerRegistration | null = null

export function setSWRegistration(registration: ServiceWorkerRegistration | null): void {
  currentRegistration = registration
}

export function getSWRegistration(): ServiceWorkerRegistration | null {
  return currentRegistration
}

// Schedules a one-shot fallback so a stuck SW can't strand the modal forever.
// Idempotent: if a timeout is already pending, this is a no-op.
export function scheduleStuckTimeout(handler: () => void, ms: number): void {
  if (stuckTimeout !== null) return
  stuckTimeout = setTimeout(() => {
    stuckTimeout = null
    handler()
  }, ms)
}

function clearStuckTimeout(): void {
  if (stuckTimeout !== null) {
    clearTimeout(stuckTimeout)
    stuckTimeout = null
  }
}

function startProgressTimer(targetPercent: number, durationMs: number, onDone?: () => void): void {
  if (progressTimer !== null) {
    clearInterval(progressTimer)
  }
  const startTime = Date.now()
  const startProgress = store.progress

  progressTimer = setInterval(() => {
    const elapsed = Date.now() - startTime
    const t = Math.min(elapsed / durationMs, 1)
    // Ease-out cubic: starts fast, slows near target
    const eased = 1 - Math.pow(1 - t, 3)
    const next = startProgress + (targetPercent - startProgress) * eased
    setStore({ progress: next })

    if (t >= 1) {
      if (progressTimer !== null) {
        clearInterval(progressTimer)
        progressTimer = null
      }
      onDone?.()
    }
  }, 50)
}

/**
 * Called by main.tsx when the SW signals a new version is installing.
 * Transitions to 'updating' and starts the fake progress bar (0 → 90% over 45s).
 */
export function notifyUpdateAvailable(): void {
  if (store.state !== 'idle') return
  setStore({ state: 'updating', progress: 0 })
  startProgressTimer(90, 45_000)
}

/**
 * Called by main.tsx when the new SW finishes installing (waiting state), or by
 * versionChecker.ts for the stuck-SW fallback.
 *
 * @param quickMode - When true (stuck-SW path), runs a short ~2s bar then reloads.
 *                    When false (SW path), jumps bar to 100%, skipWaiting the new
 *                    SW, then reloads once it takes control.
 */
export function notifyUpdateReady(quickMode = false): void {
  if (store.state === 'ready' || store.state === 'reloading') return
  clearStuckTimeout()
  if (quickMode) {
    // Stuck-SW fallback: full cycle from scratch with a short duration
    if (store.state === 'idle') {
      setStore({ state: 'updating', progress: 0 })
    }
    startProgressTimer(100, 2_000, () => {
      setStore({ state: 'reloading' })
      window.location.reload()
    })
  } else {
    // SW path: bar was already running, jump to 100% and reload once the new
    // SW takes control so the reloaded page is served by the new bundle.
    if (progressTimer !== null) {
      clearInterval(progressTimer)
      progressTimer = null
    }
    setStore({ state: 'ready', progress: 100 })

    const waiting = currentRegistration?.waiting
    if (waiting) {
      navigator.serviceWorker.addEventListener(
        'controllerchange',
        () => {
          setStore({ state: 'reloading' })
          window.location.reload()
        },
        { once: true }
      )
      setTimeout(() => {
        waiting.postMessage({ type: 'SKIP_WAITING' })
      }, 400)
    } else {
      setTimeout(() => {
        setStore({ state: 'reloading' })
        window.location.reload()
      }, 400)
    }
  }
}

/**
 * React hook — subscribes to the module-level store.
 * Lazy initializer ensures the snapshot is current at render time.
 */
export function useAppUpdate(): AppUpdateStore {
  const [snapshot, setSnapshot] = useState<AppUpdateStore>(() => store)

  useEffect(() => {
    listeners.add(setSnapshot)
    return () => {
      listeners.delete(setSnapshot)
    }
  }, [])

  return snapshot
}
